'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';

import { Typography } from 'lighthouse';

import AttributeFiltersView from '@/widgets/AttributeFilters/AttributeFiltersView';
import CatalogProductCard from '@/widgets/CatalogProductCard';
import { CatalogProductCardLoading } from '@/widgets/CatalogProductCard/CatalogProductCardLoading';
import { catalogItemFromOffer } from '@/widgets/CatalogProductCard/mappers';
import { InfiniteScrollList } from '@/widgets/InfiniteScrollList';

import { useCategoryAttributeFilters } from '@/hooks/filters/useCategoryAttributeFilters';
import { syncCityCookies } from '@/hooks/user/useSelectCity';

import { ATTRIBUTE_FILTER_COPY, ATTRIBUTE_FILTER_QUERY_KEY, BRAND_FILTER_QUERY_KEY } from '@/utilities/attributeFilters/constants';
import { getDataFromStorage } from '@/utilities/dataStorage';
import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';
import { getProductDetailsHref } from '@/utilities/getProductDetailsHref';

import { useCityStore } from '@/stores/city/useCityStore';

import { TEHRAN_CITY_ID } from '@/constants/common';
import { DEFAULT_PRODUCT_OFFER_SORT_BY } from '@/constants/productSort';

import { ProductsTabProps } from './models';

import { CityItem } from '@/client/services/geo/cities/models';
import { getFilteredProductsService } from '@/client/services/vendor/methods';
import { OfferContent, ProductOfferSortBy } from '@/server/services/vendor/models';
import { Category2, Shop } from 'iconsax-react';

const getStoredCity = (): CityItem | null => {
    try {
        return JSON.parse(getDataFromStorage('userCity', 'local'));
    } catch {
        return null;
    }
};

export const ProductsTab = ({ categories, defaultCategoryId, lastPage, offers, storeUuid, initialCityId = TEHRAN_CITY_ID }: ProductsTabProps) => {
    const searchParams = useSearchParams();
    const cityId = useCityStore((state) => state.cityId);
    const setCityId = useCityStore((state) => state.setCityId);
    const hasUrlFilters = searchParams.getAll(ATTRIBUTE_FILTER_QUERY_KEY).length > 0 || searchParams.getAll(BRAND_FILTER_QUERY_KEY).length > 0;
    const activeCategoryIdRef = useRef(defaultCategoryId);
    const cityIdRef = useRef(cityId);
    const attributeFilterParamsRef = useRef<string[]>([]);
    const brandIdsRef = useRef<number[]>([]);
    const requestIdRef = useRef(0);
    const [isCityReady, setIsCityReady] = useState(false);
    const [activeCategoryId, setActiveCategoryId] = useState(defaultCategoryId);
    const [defaultOffers, setDefaultOffers] = useState<OfferContent[]>(() => (hasUrlFilters ? [] : offers));
    const [isLastPage, setIsLastPage] = useState(lastPage);
    const [isLoading, setIsLoading] = useState(hasUrlFilters);
    const [hasProducts, setHasProducts] = useState(() => !hasUrlFilters && Boolean(offers.length));
    const [resolvedKey, setResolvedKey] = useState<string | null>(() =>
        hasUrlFilters ? null : `${initialCityId}-${defaultCategoryId}--${DEFAULT_PRODUCT_OFFER_SORT_BY}`
    );

    const attributeFilters = useCategoryAttributeFilters({ categoryId: activeCategoryId, withBrands: true });
    const attributeFilterKey = [attributeFilters.attributeFilterParams.join('|'), attributeFilters.brandIds.join(',')].join('#');
    const [sortBy, setSortBy] = useState<ProductOfferSortBy>(DEFAULT_PRODUCT_OFFER_SORT_BY);
    const sortByRef = useRef(sortBy);
    const requestKey = `${cityId}-${activeCategoryId}-${attributeFilterKey}-${sortBy}`;

    useLayoutEffect(() => {
        const storedCity = getStoredCity();

        if (storedCity?.id) {
            syncCityCookies(storedCity);

            const location: [number, number] | undefined =
                typeof storedCity.latitude === 'number' && typeof storedCity.longitude === 'number'
                    ? [storedCity.latitude, storedCity.longitude]
                    : undefined;

            if (useCityStore.getState().cityId !== storedCity.id) {
                setCityId(storedCity.id, location);
            }
        }

        setIsCityReady(true);
    }, [setCityId]);

    useEffect(() => {
        cityIdRef.current = cityId;
    }, [cityId]);

    useEffect(() => {
        attributeFilterParamsRef.current = attributeFilters.attributeFilterParams;
        brandIdsRef.current = attributeFilters.brandIds;
    }, [attributeFilters.attributeFilterParams, attributeFilters.brandIds]);

    useEffect(() => {
        sortByRef.current = sortBy;
    }, [sortBy]);

    const handleGetFilteredProducts = async (page: number, requestCityId = cityIdRef.current) => {
        if (!activeCategoryId) {
            return [];
        }

        const requestedCategoryId = activeCategoryId;
        const requestedAttributeFilters = attributeFilterParamsRef.current;
        const requestedBrandIds = brandIdsRef.current;
        const requestedSortBy = sortByRef.current;
        activeCategoryIdRef.current = activeCategoryId;
        const requestId = ++requestIdRef.current;

        if (page === 0) {
            setIsLoading(true);
        }

        try {
            const response = await getFilteredProductsService({
                page,
                size: 10,
                sortBy: requestedSortBy,
                storeUuid,
                categoryIds: [requestedCategoryId],
                cityId: requestCityId || TEHRAN_CITY_ID,
                ...(requestedAttributeFilters.length ? { attributeFilter: requestedAttributeFilters } : {}),
                ...(requestedBrandIds.length ? { brandIds: requestedBrandIds } : {})
            });

            const isStale =
                activeCategoryIdRef.current !== requestedCategoryId ||
                cityIdRef.current !== requestCityId ||
                requestId !== requestIdRef.current ||
                attributeFilterParamsRef.current.join('|') !== requestedAttributeFilters.join('|') ||
                brandIdsRef.current.join(',') !== requestedBrandIds.join(',') ||
                sortByRef.current !== requestedSortBy;

            if (isStale) {
                return [];
            }

            setIsLastPage(response.data.last);

            if (page === 0) {
                setHasProducts(Boolean(response.data.content.length));
            }

            return response.data.content;
        } catch {
            if (
                page === 0 &&
                activeCategoryIdRef.current === requestedCategoryId &&
                cityIdRef.current === requestCityId &&
                requestId === requestIdRef.current
            ) {
                setHasProducts(false);
            }

            return [];
        } finally {
            if (
                page === 0 &&
                activeCategoryIdRef.current === requestedCategoryId &&
                cityIdRef.current === requestCityId &&
                requestId === requestIdRef.current
            ) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!isCityReady || !activeCategoryId || attributeFilters.isLoading) {
            return;
        }

        activeCategoryIdRef.current = activeCategoryId;

        if (requestKey === resolvedKey) {
            return;
        }

        let isCurrentRequest = true;
        const requestedCityId = cityId;
        const requestedKey = requestKey;

        const fetchProducts = async () => {
            setDefaultOffers([]);
            setIsLastPage(false);

            const products = await handleGetFilteredProducts(0, requestedCityId);

            if (isCurrentRequest) {
                setDefaultOffers(products);
                setResolvedKey(requestedKey);
            }
        };

        void fetchProducts();

        return () => {
            isCurrentRequest = false;
        };
    }, [activeCategoryId, attributeFilters.isLoading, cityId, isCityReady, requestKey, resolvedKey]);

    const categoryNav = {
        items: categories.map((category) => ({
            id: String(category.id),
            label: category.name,
            image: GenerateFilePreviewUrl({
                attachableId: String(category.id),
                attachment: 'CATEGORY',
                type: 'MAIN',
                isPrimary: true,
                sortOrder: 0,
                thumbnail: true
            }).src
        })),
        activeId: activeCategoryId == null ? null : String(activeCategoryId),
        hideBreadcrumb: true,
        onSelect: (categoryId: string) => setActiveCategoryId(Number(categoryId))
    };
    const isListLoading =
        !isCityReady ||
        isLoading ||
        (hasUrlFilters && attributeFilters.isLoading) ||
        (Boolean(activeCategoryId) && !attributeFilters.isLoading && resolvedKey !== requestKey);

    if (!categories.length) {
        return (
            <div className='flex flex-col items-center justify-center gap-2.5 px-4 py-10 text-center'>
                <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-N50 text-blackLowEmphasis'>
                    <Category2 size={24} color='currentcolor' aria-hidden />
                </div>
                <Typography color='blackLowEmphasis' className='!m-0 text-sm'>
                    دسته‌بندی‌ای برای این فروشگاه یافت نشد.
                </Typography>
            </div>
        );
    }

    return (
        <div className='flex flex-col bg-white px-3 pb-4 pt-3 sm:px-4 sm:pb-5 sm:pt-4'>
            <AttributeFiltersView filters={attributeFilters} sortBy={sortBy} onSortChange={setSortBy} categoryNav={categoryNav}>
                {isListLoading && (
                    <div className='grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                        <CatalogProductCardLoading />
                    </div>
                )}

                {!isListLoading && !hasProducts && (
                    <div
                        className='flex flex-col items-center justify-center gap-2.5 rounded-lg border border-dashed border-N300 bg-white px-4 py-10
                            text-center'>
                        <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-N50 text-blackLowEmphasis'>
                            <Shop size={24} color='currentcolor' aria-hidden />
                        </div>
                        <Typography color='blackLowEmphasis' className='!m-0 text-sm'>
                            {attributeFilters.activeCount ? ATTRIBUTE_FILTER_COPY.emptyResults : 'محصولی برای این دسته‌بندی در شهر شما موجود نیست.'}
                        </Typography>
                    </div>
                )}

                {!isListLoading && hasProducts && (
                    <InfiniteScrollList<OfferContent>
                        key={`${activeCategoryId}-${cityId}-${attributeFilterKey}-${sortBy}`}
                        fetchData={handleGetFilteredProducts}
                        renderRow={(product) => {
                            if (product.discountPercentage === 99) {
                                return null;
                            }

                            return (
                                <CatalogProductCard
                                    key={product.productUuid}
                                    item={catalogItemFromOffer(product, getProductDetailsHref(product.productUuid, product.productSlug, storeUuid))}
                                    showSelectButton={false}
                                />
                            );
                        }}
                        containerProps={{
                            className: 'contents'
                        }}
                        endMessage={null}
                        infiniteScrollProps={{
                            className: 'grid min-w-0 w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3',
                            loader: <CatalogProductCardLoading count={6} />,
                            scrollThreshold: 0.4
                        }}
                        defaultItems={defaultOffers}
                        isLastPage={isLastPage}
                    />
                )}
            </AttributeFiltersView>
        </div>
    );
};
